// UpdateUserProfileCard.tsx
import { Box, Button, Card, Divider, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import React, { useMemo, useState } from "react";
import { usePutUserProfileMutation } from "../../../openapi";
import { selectRtkWhoami } from "../../globalSelectors";
import { useAppSelector } from "../../hooks";

const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Group justify="space-between" align="center">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Text fw={600}>{(value ?? "").trim() ? value : "–"}</Text>
  </Group>
);

const UpdateUserProfileCard: React.FC = () => {
  const user = useAppSelector(selectRtkWhoami);

  // edit state (en knapp styr alla fält)
  const [editMode, setEditMode] = useState(false);

  // form state
  const [name, setName] = useState(user?.name ?? "");
  const [location, setLocation] = useState(user?.location ?? "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber ?? "");

  const [mutate, { isLoading }] = usePutUserProfileMutation();

  // Beräkna patch-objektet med endast ändrade fält
  const { patch, hasChanges, nameWouldBeEmpty } = useMemo(() => {
    const p: Record<string, string> = {};

    const curName = user?.name ?? "";
    const curLoc = user?.location ?? "";
    const curPhone = user?.phoneNumber ?? "";

    // name – kräver icke-tomt värde om det ändras
    const nextNameTrim = name.trim();
    const nameChanged = nextNameTrim !== curName;
    if (nameChanged) p.name = nextNameTrim;

    // location – får vara tomt om användaren vill rensa
    const nextLoc = location.trim();
    if (nextLoc !== curLoc) p.location = nextLoc;

    // phoneNumber – får vara tomt
    const nextPhone = phoneNumber.trim();
    if (nextPhone !== curPhone) p.phoneNumber = nextPhone;

    return {
      patch: p,
      hasChanges: Object.keys(p).length > 0,
      nameWouldBeEmpty: nameChanged && nextNameTrim.length === 0,
    };
  }, [name, location, phoneNumber, user]);

  const canSave = hasChanges && !nameWouldBeEmpty;

  const onSaveAll = async () => {
    await mutate({
      body: {
        userId: user._id,
        patch, // { name?, location?, phoneNumber? }
      },
    });
    setEditMode(false);
  };

  const onCancel = () => {
    setName(user?.name ?? "");
    setLocation(user?.location ?? "");
    setPhoneNumber(user?.phoneNumber ?? "");
    setEditMode(false);
  };

  return (
    <Card withBorder radius="lg" shadow="sm">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={5} m={0}>
            Profil
          </Title>

          {!editMode ? (
            <Button leftSection={<IconEdit size={16} />} variant="filled" onClick={() => setEditMode(true)}>
              Rediger
            </Button>
          ) : (
            <Group>
              <Button variant="outline" onClick={onCancel}>
                Avbryt
              </Button>
              <Button variant="filled" onClick={onSaveAll} loading={isLoading} disabled={!canSave}>
                Lagre
              </Button>
            </Group>
          )}
        </Group>

        <Divider />

        {!editMode ? (
          <Stack gap="sm">
            <Row label="Navn" value={user?.name} />
            <Row label="Sted" value={user?.location} />
            <Row label="Telefonnummer" value={user?.phoneNumber} />
          </Stack>
        ) : (
          <Stack gap="sm">
            <Box>
              <Text size="sm" c="dimmed">
                Navn
              </Text>
              <TextInput
                placeholder="Navn"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                mt={4}
                autoFocus
                error={nameWouldBeEmpty ? "Navn kan ikke være tomt" : undefined}
              />
            </Box>

            <Box>
              <Text size="sm" c="dimmed">
                Sted
              </Text>
              <TextInput
                placeholder="F.eks. Oslo"
                value={location}
                onChange={(e) => setLocation(e.currentTarget.value)}
                mt={4}
              />
            </Box>

            <Box>
              <Text size="sm" c="dimmed">
                Telefonnummer
              </Text>
              <TextInput
                placeholder="F.eks. +47 400 00 000"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                mt={4}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default UpdateUserProfileCard;
